import Grid from '@mui/material/Grid';
import type {
  InventoryAndContentState,
  InventoryContentIdsTypeOption,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { useTranslation } from 'react-i18next';
import type { AnyAction } from 'redux';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle';
import { INVENTORY_IDS_DISPLAY_LIMIT, useInventoryAndContentLabels } from '../../shared/InventoryAndContent/constants';
import {
  resetFilter,
  setComparisonOpValue,
  setContentCategoryOp,
} from '../../shared/InventoryAndContent/inventoryHelpers';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems';
import { Switch } from '../../shared/Switch/Switch';
import type { TargetingItemsProps } from '../../types';

interface InventoryIDsProps<T> extends TargetingItemsProps<T> {
  option: InventoryContentIdsTypeOption;
}

export const InventoryIDs = ({
  dispatchInventory = noop,
  readonly = true,
  targetingParams,
  option,
  isNotLastItem,
  switchState: chipLineSwitchState,
  dataTest,
  showDimensionChipLine = true,
  disabledReasonMessage,
}: InventoryIDsProps<Omit<InventoryAndContentState, typeof InventoryContentOption.PUBLISHER_INVENTORY_RULES>>) => {
  const { t } = useTranslation();

  const optionPayload = targetingParams[option];
  const { op: dimension, val } = optionPayload;

  const labels = useInventoryAndContentLabels();
  const label = labels[option].label;

  const switchState = dimension === Intersect.NOT_INTERSECTS;
  const filtersTitle = dimension === Intersect.NOT_INTERSECTS ? t('blocked') : t('allowed');
  const itemsCount = val.length;

  return (
    <Grid data-test="sub-section">
      <FiltersTitle
        onClear={() =>
          dispatchInventory({
            payload: {
              contentOption: option,
            },
            type: resetFilter,
          } as unknown as AnyAction)
        }
        title={`${label} [ ${itemsCount} ]`}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />
      {!readonly && (
        <Switch
          switchState={switchState}
          onSwitchChange={() =>
            dispatchInventory({
              payload: switchState ? Intersect.INTERSECTS : Intersect.NOT_INTERSECTS,
              type: setContentCategoryOp[option],
            } as unknown as AnyAction)
          }
          switchOffLabel={t('allowed')}
          switchOnLabel={t('blocked')}
        />
      )}

      <Grid data-test="section-items">
        <SelectedItems
          filtersTitle={filtersTitle}
          isAddIcon={dimension === Intersect.INTERSECTS}
          selectedItems={val}
          readonly={readonly}
          handleChange={values => {
            dispatchInventory({
              payload: values,
              type: setComparisonOpValue[option],
            } as unknown as AnyAction);
          }}
          isRemovingAll={true}
          customDisplayLimit={INVENTORY_IDS_DISPLAY_LIMIT}
          dataTest={dataTest}
        />
      </Grid>
      {isNotLastItem && showDimensionChipLine && <DimensionChipLine switchState={chipLineSwitchState} />}
    </Grid>
  );
};
